import React, { createContext, useState, ReactNode, useEffect } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { useLightTheme, useDarkTheme } from "../theme";

interface ThemeContextProps {
  toggleTheme: () => void;
  themeMode: "light" | "dark";
}

export const ThemeContext = createContext<ThemeContextProps | undefined>(
  undefined
);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const lightTheme = useLightTheme();
  const darkTheme = useDarkTheme();

  // Initialize theme mode based on the current hour
  const [themeMode, setThemeMode] = useState<"light" | "dark">(() => {
    const hour = new Date().getHours();
    return hour >= 6 && hour < 23 ? "light" : "dark";
  });

  const theme = themeMode === "light" ? lightTheme : darkTheme;

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  useEffect(() => {
    const now = new Date();
    const isDayTime = now.getHours() >= 6 && now.getHours() < 23;

    // Calculate the time until the next theme change (either 6 AM or 6 PM)
    const nextChange = isDayTime
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 0, 0) // 6 PM
      : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 6, 0, 0); // 6 AM next day

    const timeout = nextChange.getTime() - now.getTime();

    // Set a timer for the next theme change
    const timer = setTimeout(() => {
      setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    }, timeout);

    // Cleanup timer on component unmount or re-render
    return () => clearTimeout(timer);
  }, [themeMode]);

  return (
    <ThemeContext.Provider value={{ toggleTheme, themeMode }}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
